// Admin portal shell. Mirrors the mymcflex AdminShell pattern: 260 px white
// sidebar, grouped nav with uppercase section eyebrows, active item lit in
// gold-tint / gold-dark (vs mymcflex's red), signed-in-as block + sign-out at
// the bottom. Page content renders into <Outlet />.
//
// Phase 4 only wires the structure — every nav target lands on a stub via
// AdminPlaceholder. Server-side role gating on /api/admin/* will come with
// the actual page content; for now, AdminGate in App.tsx is the boundary.
import { useEffect, useState } from 'react'
import { NavLink, Outlet, useLocation, Link } from 'react-router-dom'
import { useAuth } from '@workos-inc/authkit-react'
import ScrollToTop from './ScrollToTop'
import { AI_TOOLS_ENABLED } from '../lib/features'

interface NavItem { to: string; label: string; end?: boolean; restrictedToEmail?: string; hidden?: boolean }
interface NavGroup { label: string; items: NavItem[] }

// Menu structure pulled verbatim from the EVRST Prints backend menu spec
// (uploaded 2026-05-28). Keep the labels exactly as specified — don't
// simplify or rename without an explicit go-ahead.
export const NAV_GROUPS: NavGroup[] = [
  {
    label: 'Processing',
    items: [
      { to: '/admin/processing',      label: 'Dashboard' },
      { to: '/admin/quotes',          label: 'Quotes' },
      { to: '/admin/orders',          label: 'Orders' },
      { to: '/admin/purchase-orders', label: 'Purchase Order' },
      { to: '/admin/abandoned-carts', label: 'Abandoned Carts' },
      { to: '/admin/payments',        label: 'Payments' },
    ],
  },
  {
    label: 'Production',
    items: [
      { to: '/admin',                  end: true, label: 'Overview' },
      { to: '/admin/artwork-approval', label: 'Artwork Approval' },
      { to: '/admin/production',       label: 'Production' },
      { to: '/admin/shipping',         label: 'Shipping' },
      { to: '/admin/inventory',        label: 'Inventory On Hand' },
      { to: '/admin/create-mockup',    label: 'Create a Mockup' },
      { to: '/admin/garment-resizer',  label: 'Garment Resizer' },
    ],
  },
  {
    label: 'Catalog',
    items: [
      { to: '/admin/garments',      label: 'Garments' },
      { to: '/admin/studio-media',  label: 'Media Library' },
      { to: '/admin/ai-artwork',    label: 'AI Artwork' },
    ],
  },
  {
    label: 'Customers',
    items: [
      { to: '/admin/customers',  label: 'Customers' },
      { to: '/admin/companies',  label: 'Companies' },
      { to: '/admin/statements', label: 'Account Statements' },
    ],
  },
  {
    label: 'Settings',
    items: [
      { to: '/admin/settings/how-to',         label: 'How To' },
      { to: '/admin/settings/prints-page',    label: 'Evrst Printpage' },
      { to: '/admin/settings/web-page',       label: 'Evrst Webpage' },
      { to: '/admin/settings/users',          label: 'Users' },
      { to: '/admin/settings/stripe',         label: 'Stripe' },
      { to: '/admin/settings/ai-learning',    label: 'AI Learning', restrictedToEmail: 'rex@ethx.com.au', hidden: !AI_TOOLS_ENABLED },
      { to: '/admin/settings/platform-ideas', label: 'Platform Ideas' },
    ],
  },
]

// Like mymcflex's `activeNavTo`: find the single longest-prefix-matching
// item so deep links (e.g. /admin/orders/123) light up the right parent.
const ALL_ITEMS = NAV_GROUPS.flatMap((g) => g.items)
function activeNavTo(pathname: string): string | null {
  let best: string | null = null
  let bestScore = -1
  for (const it of ALL_ITEMS) {
    const p = it.to
    const exactRoot = p === '/admin' && pathname === '/admin'
    const prefix = p !== '/admin' && (pathname === p || pathname.startsWith(p + '/'))
    if (!exactRoot && !prefix) continue
    const score = exactRoot ? 0 : p.length
    if (score > bestScore) { bestScore = score; best = p }
  }
  return best
}

interface ServerMe { email: string; firstName: string | null; lastName: string | null; role: string }

export default function AdminShell() {
  const { user: authkitUser, signOut } = useAuth()
  const [serverMe, setServerMe] = useState<ServerMe | null>(null)
  const location = useLocation()
  const activeTo = activeNavTo(location.pathname)

  // Read the local user mirror via /api/me. This is the only state that's
  // populated when sign-in came via /auth/login (server cookie flow) rather
  // than the AuthKit React SDK's in-browser flow.
  useEffect(() => {
    let mounted = true
    fetch('/api/me', { credentials: 'include' })
      .then((r) => r.json())
      .then((d) => { if (mounted && d?.authenticated) setServerMe(d.user as ServerMe) })
      .catch(() => { /* offline / 401 — sidebar will fall back to AuthKit user */ })
    return () => { mounted = false }
  }, [])

  const user = authkitUser ?? serverMe
  // Sign-out clears BOTH auth states: our sealed cookie + the AuthKit SDK's.
  async function doSignOut() {
    try { await fetch('/auth/logout', { credentials: 'include' }) } catch { /* ignore */ }
    try { await signOut({ returnTo: window.location.origin + '/' }) }
    catch { window.location.assign('/') }
  }

  return (
    <div className="min-h-screen grid grid-cols-1 md:grid-cols-[260px_1fr] bg-surface">
      <ScrollToTop />

      {/* SIDEBAR */}
      <aside className="border-r border-border bg-white flex flex-col">
        <div className="p-5 border-b border-border">
          <Link to="/" className="block">
            <div className="font-heading uppercase font-bold tracking-[0.18em] text-base text-ink">
              EVRST<span className="text-gold">.</span>PRINTS
            </div>
            <div className="text-[10px] text-ink-3 leading-tight uppercase tracking-[0.18em] mt-1">Admin portal</div>
          </Link>
        </div>

        <nav className="p-3 space-y-5 flex-1 overflow-y-auto">
          {NAV_GROUPS.map((g) => (
            <div key={g.label}>
              <div className="px-2 text-[10px] font-bold tracking-[0.16em] uppercase text-ink-3 mb-1.5">{g.label}</div>
              <ul className="space-y-0.5">
                {g.items
                  .filter((item) => !item.hidden)
                  .filter((item) => !item.restrictedToEmail || serverMe?.email === item.restrictedToEmail)
                  .map((item) => (
                    <li key={item.to}>
                      <NavLink
                        to={item.to}
                        end={item.end}
                        className={
                          'flex items-center justify-between px-3 py-2 rounded text-sm font-medium transition-colors ' +
                          (item.to === activeTo ? 'bg-gold/15 text-gold-dark' : 'text-ink-2 hover:bg-surface')
                        }
                      >
                        <span>{item.label}</span>
                      </NavLink>
                    </li>
                  ))}
                {/* "Logout" in the Settings spec is a sign-out action, not a route. */}
                {g.label === 'Settings' && (
                  <li>
                    <button
                      onClick={doSignOut}
                      className="w-full flex items-center justify-between px-3 py-2 rounded text-sm font-medium text-ink-2 hover:bg-surface transition-colors"
                    >
                      <span>Logout</span>
                    </button>
                  </li>
                )}
              </ul>
            </div>
          ))}
        </nav>

        <div className="p-4 border-t border-border">
          <div className="text-[10px] uppercase tracking-[0.16em] text-ink-3">Signed in as</div>
          <div className="text-sm font-semibold text-ink truncate mt-1">{user?.firstName} {user?.lastName}</div>
          <div className="text-xs text-ink-3 truncate">{user?.email}</div>
          <Link to="/" className="block text-xs text-ink-3 hover:text-gold-dark mt-3">← Back to public site</Link>
        </div>
      </aside>

      {/* MAIN */}
      <main className="bg-surface min-h-screen overflow-x-hidden">
        <Outlet />
      </main>
    </div>
  )
}
